import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Chip, DialogTitle, Typography } from '@mui/material';
import {
  fetchReportTemplateData, deleteReportTemplate,
  setSelectedReportTemplate, clearReportTemplateData,
} from '../reduxStore/ReportTemplateSlice';
import { AppDispatch } from '../reduxStore/Store';
import MyTable from '../components/MyTable';
import { ButtonType } from '../constants/enums';
import { TableHeader, TableAction } from '../constants/interfaces';
import { ReportTemplate } from '../models/models';
import ReportTemplateForm from '../components/Forms/ReportTemplateForm';
import { useDialog } from '../components/DialogComponent';
import { useSnackbar } from '../components/SnackbarProvider';
import DescriptionIcon from '@mui/icons-material/Description';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';

const GestionReportTemplate: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { reportTemplateData, total, loading, error, successMessage } =
    useSelector((state: any) => state.ReportTemplateSlice);
  const selectedReportTemplate = useSelector((state: any) => state.ReportTemplateSlice.selectedReportTemplate);

  const [fetchParams, setFetchParams] = useState({ limit: 10, offset: 0 });
  const [templateToDelete, setTemplateToDelete] = useState<ReportTemplate | null>(null);
  const [FormDialog, openDialog, closeDialog] = useDialog({ dialogProps: { maxWidth: 'md', fullWidth: true } });
  const [ConfirmDialog, openConfirmDialog, closeConfirmDialog] = useDialog({ modal: true });

  useEffect(() => {
    dispatch(fetchReportTemplateData({ ...fetchParams, type_rapport: null }));
    return () => {
      dispatch(setSelectedReportTemplate(null));
      dispatch(clearReportTemplateData());
    };
  }, [dispatch, fetchParams, successMessage]);

  useEffect(() => {
    if (error)          showSnackbar(error, 'error');
    if (successMessage) showSnackbar(successMessage, 'success');
  }, [error, successMessage, dispatch, showSnackbar]);

  const handleCancel = () => {
    closeDialog();
    dispatch(setSelectedReportTemplate(null));
  };

  const headers: TableHeader<ReportTemplate>[] = [
    { key: 'nom',          label: 'Nom' },
    { key: 'type_rapport', label: 'Type',       render: (row: any) => row.type_rapport ?? 'Tous' },
    { key: 'is_default',   label: 'Défaut',     render: (row: any) => row.is_default ? <Chip label="Défaut" color="primary" size="small" /> : null },
    { key: 'updated_at',   label: 'Modifié le', render: (row: any) => row.updated_at ? new Date(row.updated_at).toLocaleDateString('fr-FR') : '' },
  ];

  const tableActions: TableAction<ReportTemplate>[] = [
    {
      label: 'Modifier',
      type: ButtonType.EDIT,
      callback: (t) => { dispatch(setSelectedReportTemplate(t)); openDialog(); },
    },
    {
      label: 'Supprimer',
      type: ButtonType.DELETE,
      callback: (t) => { setTemplateToDelete(t); openConfirmDialog(); },
    },
  ];

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title="Templates de rapports"
        subtitle={`${total} template${total > 1 ? 's' : ''}`}
        icon={<DescriptionIcon />}
        addLabel="Nouveau template"
        onAdd={openDialog}
      />

      {loading && <Typography color="#667085" mb={2}>Chargement...</Typography>}

      <AppSurface>
        <MyTable
          headers={headers}
          data={reportTemplateData}
          actions={tableActions}
          onPageChange={(page, rows) => setFetchParams({ limit: rows, offset: page * rows })}
          totalRows={total}
        />
      </AppSurface>

      <FormDialog>
        <DialogTitle>
          {selectedReportTemplate ? 'Modifier le template' : 'Nouveau template'}
        </DialogTitle>
        <ReportTemplateForm onCancel={handleCancel} />
      </FormDialog>

      <ConfirmDialog>
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Supprimer ce template ?</Typography>
          <Typography sx={{ mb: 3 }}>
            Le template <strong>{templateToDelete?.nom}</strong> sera supprimé définitivement.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={closeConfirmDialog}>Annuler</Button>
            <Button variant="contained" color="error" onClick={() => {
              dispatch(deleteReportTemplate(templateToDelete!.id!));
              closeConfirmDialog();
            }}>Supprimer</Button>
          </Box>
        </Box>
      </ConfirmDialog>
    </AppPage>
  );
};

export default GestionReportTemplate;
